/**
 * Helper functions for processing booking logs
 */

/**
 * Extracts and formats a user-friendly resource name from booking log data
 *
 * @param {Object} log - The booking log entry
 * @param {Array} resources - Available resources (lab sessions or equipment) to match against
 * @returns {String} A user-friendly resource name
 */
export const getResourceNameFromLog = (log, resources = []) => {
  // Check for direct resource name in the log
  if (log.resourceName && typeof log.resourceName === "string") {
    return log.resourceName;
  }

  // Check if resourceId is an object with name property
  if (
    log.resourceId &&
    typeof log.resourceId === "object" &&
    log.resourceId.name
  ) {
    return log.resourceId.name;
  }

  // Try to match resourceId with resources
  let resourceIdValue = log.resourceId;
  if (typeof resourceIdValue === "object" && resourceIdValue !== null) {
    // Extract ID from object if it doesn't have a name
    resourceIdValue =
      resourceIdValue.id || resourceIdValue._id || resourceIdValue;
  }

  // Try to find matching resource by ID
  if (resourceIdValue && resources && resources.length > 0) {
    // Try exact ID match
    const exactMatch = resources.find(
      (r) =>
        (r._id && r._id.toString() === resourceIdValue.toString()) ||
        (r.id && r.id.toString() === resourceIdValue.toString())
    );

    if (exactMatch) {
      return exactMatch.name;
    }

    // Try partial ID match
    const partialMatch = resources.find((r) => {
      const resourceId = (r._id || r.id || "").toString();
      return (
        resourceId.includes(resourceIdValue.toString()) ||
        resourceIdValue.toString().includes(resourceId)
      );
    });

    if (partialMatch) {
      return partialMatch.name;
    }
  }

  // Return a generic resource type with ID if available
  const resourceType = log.resourceType === "lab_session" ? "Lab" : "Equipment";
  return resourceIdValue
    ? `${resourceType} ${resourceIdValue.toString().substring(0, 8)}`
    : `Unknown ${resourceType}`;
};

/**
 * Extracts and formats a user-friendly user name from booking log data
 *
 * @param {Object} log - The booking log entry
 * @param {Array} users - Array of all user objects (optional)
 * @param {Object} currentUser - Current logged in user for reference (optional)
 * @returns {String} A user-friendly user name
 */
export const getUserNameFromLog = (log, users = [], currentUser = null) => {
  // 1. Try to resolve from users array if userId is present
  if (log.userId) {
    // Handle userId as an object (which is common in booking logs)
    const id =
      typeof log.userId === "object"
        ? log.userId._id || log.userId.id || log.userId.uid || log.userId
        : log.userId;

    if (users && Array.isArray(users) && users.length > 0) {
      // Try to find user by any ID field - match against firebaseUid too
      const found = users.find(
        (u) =>
          u._id === id ||
          u.id === id ||
          u.uid === id ||
          u.firebaseUid === id ||
          (u.authId && u.authId === id)
      );

      if (found) {
        return (
          found.displayName ||
          found.name ||
          (found.email ? found.email.split("@")[0] : null) ||
          "Unknown User"
        );
      }
    }
  }

  // 2. If userId is an object with name property (structure in booking logs)
  if (log.userId && typeof log.userId === "object") {
    if (log.userId.name) return log.userId.name;
    if (log.userId.displayName) return log.userId.displayName;
    if (log.userId.email) return log.userId.email.split("@")[0];
  }

  // 3. Check for user field or userDisplayName/userName directly in the log
  if (log.user && typeof log.user === "object") {
    return (
      log.user.name || log.user.displayName || log.user.email || "Unknown User"
    );
  }
  if (log.userDisplayName) return log.userDisplayName;
  if (log.userName) return log.userName;

  // 4. Check approvedBy field for status changes
  if (log.activityType && ["approved", "rejected"].includes(log.activityType)) {
    if (log.approvedBy) {
      if (typeof log.approvedBy === "object") {
        return (
          log.approvedBy.name || log.approvedBy.displayName || "Staff Member"
        );
      }
      return log.approvedBy;
    }
  }

  // 5. If userId is a string and might be an email
  if (log.userId && typeof log.userId === "string") {
    if (log.userId.includes("@")) {
      return log.userId.split("@")[0];
    }

    // Check if it matches current user
    if (currentUser && log.userId === currentUser.id) {
      return currentUser.displayName || currentUser.name || "You";
    }

    return `User (${log.userId.substring(0, 8)})`;
  }

  // 6. Check for department if all else fails
  if (log.department) {
    return `${log.department} User`;
  }

  return "Unknown User";
};

/**
 * Formats the activity type for display
 *
 * @param {Object} log - The booking log entry
 * @returns {Object} Activity type information with label and color
 */
export const formatActivityType = (log) => {
  const activityType = log.activityType || "modified";

  // Determine the appropriate label and color
  let color = "default";
  let label = activityType.charAt(0).toUpperCase() + activityType.slice(1);

  if (activityType === "created") {
    color = "info";
  } else if (activityType === "approved") {
    color = "success";
  } else if (activityType === "rejected" || activityType === "cancelled") {
    color = "error";
  } else if (activityType === "completed") {
    color = "secondary";
  } else if (activityType === "modified") {
    color = "primary";
  }

  return { label, color };
};
